import { ChevronLeft, ChevronRight, Edit2 } from 'lucide-react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { BundleToolbar } from '@/components/bundle/BundleToolbar';
import { countResponseFiles, responseVolumes } from '@/components/bundle/bundleTypes';
import { GenerateBundleDialog } from '@/components/bundle/GenerateBundleDialog';
import { GeneratedDocsDialog } from '@/components/bundle/GeneratedDocsDialog';
import { ViewBundleTree } from '@/components/bundle/view/ViewBundleTree';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import Loading from '@/components/shared/Loading';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { Button } from '@/components/ui/NewButton';
import { useBundle } from '@/hooks/useBundle';
import { useBundlePreviewPanel } from '@/hooks/useBundlePreviewPanel';
import { hydrateBundleTree, useBundleRemoteRefresh } from '@/hooks/useBundleRemoteRefresh';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { type BundleBase, bundlePaths } from '@/lib/bundleRoutes';

const BundleError = ({ message, slug, base }: { message: string; slug: string; base: BundleBase }) => {
  const navigate = useNavigate();
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2">
      <p className="text-sm text-gray-500">{message}</p>
      <Button variant="ghost" size="sm" icon={ChevronLeft} text={ui_labels.bundle_back} onClick={() => navigate({ to: bundlePaths(base).index, params: { slug } })} />
    </div>
  );
};

const BundleViewerEditor = ({ slug, bundleId, base }: { slug: string; bundleId: string; base: BundleBase }) => {
  const { selectedCase } = useCaseContext();
  const navigate = useNavigate();
  const paths = bundlePaths(base);
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const slugStr = slug;
  const bundleIdStr = bundleId;

  const { bundle, isLoading, isError, mutate } = useBundle(slugStr, bundleIdStr);

  // Only re-initialize on bundle identity change. SWR revalidations produce a new `bundle`
  // reference with the same content; re-initializing on every revalidation would clobber
  // any pending side-panel preview state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (!bundle) return;
    hydrateBundleTree(slugStr, bundle);
  }, [bundle?.bundle_id]);

  useBundleRemoteRefresh({
    bundleId: bundleIdStr,
    matterGuid: slugStr,
    mutate,
    resourceNoun: ui_labels.bundle_singular.toLowerCase(),
  });

  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [splitPerVolume, setSplitPerVolume] = useState(false);
  const [showGeneratedDocsDialog, setShowGeneratedDocsDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    handlePreviewClick,
    previewLoading,
    handlePreviewIndexClick,
    previewIndexLoading,
    handlePreviewVolume,
    previewVolumeLoadingIndex,
  } = useBundlePreviewPanel(slugStr, bundleIdStr);

  const volumes = useMemo(() => (bundle ? responseVolumes(bundle.groups) : []), [bundle]);

  const resolvedBundleTitle = bundle?.title ?? '';
  const breadcrumbSubheader = useMemo(() => (
    <span className="block">
      <span className="flex items-center text-gray-500">
        <Link to="/matter/$slug/case-files" params={{ slug: slugStr }} className="hover:text-gray-700 font-medium">
          Case Files
        </Link>
        <span className="flex items-center">
          <ChevronRight className="h-4 w-4 mx-1" />
          <Link to={paths.index} params={{ slug: slugStr }} className="hover:text-gray-700 font-medium">
            {ui_labels.bundle_plural}
          </Link>
        </span>
        <span className="flex items-center">
          <ChevronRight className="h-4 w-4 mx-1" />
          <span className="hover:text-gray-700 font-medium">{resolvedBundleTitle || ui_labels.bundle_singular}</span>
        </span>
      </span>
    </span>
  ), [resolvedBundleTitle, slugStr, ui_labels.bundle_plural, ui_labels.bundle_singular]);

  useShellHeader({
    header: resolvedBundleTitle,
    subheader: breadcrumbSubheader,
  });

  const bundleHeaderBelow = bundle ? (
    <div className="flex items-center justify-end gap-2 pb-2">
      <Button
        variant="ghost"
        size="sm"
        icon={ChevronLeft}
        text="Back"
        onClick={() => navigate({ to: paths.index, params: { slug: slugStr } })}
        className="bg-slate-100 hover:bg-slate-200"
      />
      <Link to={paths.edit} params={{ slug: slugStr, bundleId: bundleIdStr }}>
        <Button variant="outline" size="sm" text="Edit" icon={Edit2} />
      </Link>
      <BundleToolbar
        hasFiles={countResponseFiles(bundle.groups) > 0}
        onPreview={handlePreviewClick}
        previewLoading={previewLoading}
        onPreviewIndex={handlePreviewIndexClick}
        previewIndexLoading={previewIndexLoading}
        volumes={volumes}
        onPreviewVolume={handlePreviewVolume}
        previewVolumeLoadingIndex={previewVolumeLoadingIndex}
        onGenerate={() => { setSplitPerVolume(false); setShowGenerateDialog(true); }}
        onGenerateSplit={() => { setSplitPerVolume(true); setShowGenerateDialog(true); }}
        generateLoading={isGenerating}
        onGeneratedDocs={() => setShowGeneratedDocsDialog(true)}
      />
    </div>
  ) : null;

  useShellHeaderBelow(bundleHeaderBelow);

  const handleGenerate = async (filename: string, targetFolderGuid: string | null, split: boolean) => {
    setIsGenerating(true);
    try {
      await backendClient.post(`/bundle/matter/${slugStr}/${bundleIdStr}/generate`, {
        filename,
        target_folder_guid: targetFolderGuid,
        split_per_volume: split,
      });
      setShowGenerateDialog(false);
      toast.success(ui_labels.bundle_generation_started);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to start generation');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!selectedCase) return <NoCaseSelected />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  if (!bundle) {
    const status = isError?.response?.status;
    const noun = ui_labels.bundle_singular.toLowerCase();
    const message = status === 404
      ? `This ${noun} does not exist.`
      : status === 403
        ? `You do not have permission to view this ${noun}.`
        : `Failed to load ${noun}.`;
    return <BundleError message={message} slug={slugStr} base={base} />;
  }

  const description = bundle.description?.trim();

  return (
    <div className="overflow-x-hidden">
      <div className="px-6 py-8">
        <div className="mx-auto w-full max-w-(--breakpoint-xl) space-y-6">
          {description ? (
            <section className="rounded-xl border border-border bg-card px-6 py-5">
              <h2 className="text-sm font-medium text-foreground">Description</h2>
              <p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                {description}
              </p>
            </section>
          ) : null}

          {/* The child selector widens the gap inside ViewBundleTree's root flex
              column, between its "Structure" heading and the file list. */}
          <section className="rounded-xl border border-border bg-card px-6 pt-8 pb-6 [&>div]:gap-6">
            <ViewBundleTree />
          </section>
        </div>
      </div>

      <GenerateBundleDialog
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        bundleTitle={bundle.title ?? ui_labels.bundle_singular}
        matterSlug={slugStr}
        splitPerVolume={splitPerVolume}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
      />
      <GeneratedDocsDialog
        open={showGeneratedDocsDialog}
        onOpenChange={setShowGeneratedDocsDialog}
        matterSlug={slugStr}
        bundleId={bundleIdStr}
      />
    </div>
  );
};
// `binders` is the US-facing alias for `bundles`; both routes render the same page.
// Each route gets its own wrapper so params are read with a concrete, typed `from`
// (no `strict: false` + cast needed).
export const BundleDetailPage = () => {
  const { slug, bundleId } = useParams({ from: '/matter/$slug/bundles/$bundleId' });
  return <BundleViewerEditor slug={slug} bundleId={bundleId} base="bundles" />;
};

export const BinderDetailPage = () => {
  const { slug, bundleId } = useParams({ from: '/matter/$slug/binders/$bundleId' });
  return <BundleViewerEditor slug={slug} bundleId={bundleId} base="binders" />;
};

export default BundleDetailPage;
